// CrystalVault — Affiliate Dashboard
//
// Renders one of three states based on the connected wallet's affiliate state:
//   1. Not connected     → "Connect a wallet to see your affiliate dashboard"
//   2. Connected, not yet an affiliate
//                        → "You don't qualify yet. Deposit $500+ to any vault."
//   3. Connected + affiliate
//                        → Full dashboard: link(s), eligibility status, referrals,
//                          earnings paid/pending/forfeit, payout history.
//
// Reads from GET /api/affiliate/me?wallet=0x.... Endpoint returns 200 for all
// states (including non-affiliate) so we can render UI cleanly without
// distinguishing 404 from network error.

import { VAULT_CONFIGS } from '../config.js';
import { getAddress, isConnected, shortAddr } from '../wallet.js';

// Affiliate state lives in SQLite on Lightsail; Lambda (app.crystalclear.finance
// origin for /api/*) can't read it yet. Until the DDB-sync sidecar is extended
// to mirror affiliate tables, point at the Lightsail-hosted API directly.
// CORS is open. See AFFILIATE_PROGRAM_SPEC.md for the long-term DDB plan.
const API = 'https://crystalclear.finance/api/affiliate/me';
const LINK_BASE = 'https://crystalclear.finance/r/';

let _wireListenerOnce = false;

export function renderAffiliate() {
  return `
    <div class="px-6 py-10">
      <div class="flex flex-col gap-2 mb-8">
        <h1 class="font-headline font-bold text-3xl text-on-surface">Affiliate Dashboard</h1>
        <p class="text-on-surface-variant text-sm">
          Earn 50% of performance fees from people who deposit via your link.
          <a href="/affiliate-program.html" class="text-primary hover:underline">How it works →</a>
        </p>
      </div>
      <div id="aff-content">
        <div class="text-on-surface-variant text-center py-12">Loading…</div>
      </div>
    </div>
  `;
}

export function initAffiliate() {
  _refresh();
  if (!_wireListenerOnce) {
    window.addEventListener('wallet-changed', _refresh);
    _wireListenerOnce = true;
  }
}

// Re-fetch helper. Critical: must re-query the DOM after every await in
// _refresh() because the router can re-render the page (on wallet-changed
// or hashchange) while our fetch is in flight, leaving us with a stale
// detached reference. Bug caught in browser E2E test 2026-05-18: dashboard
// stayed stuck on "Loading…" forever because _refresh was writing to a
// detached aff-content node that the router had already replaced.
function _root() { return document.getElementById('aff-content'); }

async function _refresh() {
  if (!_root()) return;  // page no longer mounted

  if (!isConnected()) {
    const r = _root(); if (r) r.innerHTML = _notConnected();
    return;
  }

  const wallet = getAddress();
  {
    const r = _root(); if (r) r.innerHTML = `<div class="text-on-surface-variant text-center py-12">Loading your dashboard…</div>`;
  }

  try {
    const res = await fetch(`${API}?wallet=${encodeURIComponent(wallet)}`);
    if (res.status === 503) {
      const r = _root(); if (r) r.innerHTML = _notActive();
      return;
    }
    if (!res.ok) {
      const r = _root(); if (r) r.innerHTML = _error(`HTTP ${res.status}`);
      return;
    }
    const data = await res.json();
    const r = _root();
    if (!r) return;  // user navigated away during fetch
    if (!data.is_affiliate) {
      r.innerHTML = _notQualifiedYet(wallet);
      return;
    }
    r.innerHTML = _dashboard(data);
    _wireCopyButtons();
  } catch (e) {
    console.error('affiliate dashboard load failed:', e);
    const r = _root();
    if (r) r.innerHTML = _error(e.message || 'network error');
  }
}

// ── State renderers ───────────────────────────────────────────────────────

function _notConnected() {
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-8 text-center">
      <p class="text-on-surface mb-4">Connect a wallet to see your affiliate dashboard.</p>
      <p class="text-on-surface-variant text-sm">
        Anyone holding $500+ in any CrystalVault automatically qualifies as an affiliate.
      </p>
    </div>
  `;
}

function _notActive() {
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-8 text-center">
      <p class="text-on-surface mb-4">Affiliate program is being set up.</p>
      <p class="text-on-surface-variant text-sm">Check back soon.</p>
    </div>
  `;
}

function _notQualifiedYet(wallet) {
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-8">
      <div class="flex items-center gap-2 mb-3">
        <span class="material-symbols-outlined text-on-surface-variant">person_outline</span>
        <span class="text-on-surface-variant text-sm font-mono">${shortAddr(wallet)}</span>
      </div>
      <h2 class="font-headline text-xl text-on-surface mb-3">You don't qualify yet</h2>
      <p class="text-on-surface-variant mb-6">
        To qualify as an affiliate, deposit at least $500 into any of our vaults.
        Eligibility is checked daily from on-chain balances — no signup, no approval.
      </p>
      <a href="#vaults" class="inline-block px-4 py-2 bg-primary text-on-primary font-semibold rounded-lg hover:bg-primary-container transition-colors">
        Browse vaults
      </a>
    </div>
  `;
}

function _error(msg) {
  return `
    <div class="bg-surface-container border border-error/30 rounded-xl p-8 text-center">
      <p class="text-error mb-2">Couldn't load your dashboard.</p>
      <p class="text-on-surface-variant text-sm">${msg}</p>
    </div>
  `;
}

// ── Main dashboard ────────────────────────────────────────────────────────

function _dashboard(data) {
  const totalEligible = data.affiliations.filter(a => a.currently_eligible).length;
  const totalAffil = data.affiliations.length;
  const anyEligible = totalEligible > 0;
  const status = data.payout_status || {};
  const underfunded = status.operator_underfunded === true;
  const hasPending = (data.earnings_pending_usdc || 0) > 0;

  return `
    <div class="flex flex-col gap-6">

      ${_eligibilityBanner(data.affiliations, anyEligible)}

      ${underfunded && hasPending ? _payoutsPausedBanner(status) : ''}

      <!-- KPI row -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        ${_kpiCard('Referrals', data.referrals_count, 'group')}
        ${_kpiCard('Earned (paid)', `$${data.earnings_paid_usdc.toFixed(2)}`, 'paid')}
        ${_pendingKpiCard(data.earnings_pending_usdc, underfunded)}
      </div>

      ${_linksCard(data.affiliations)}

      ${_forfeitCard(data.forfeit_by_reason)}

      ${_payoutsTable(data.payouts)}

    </div>
  `;
}

function _payoutsPausedBanner(status) {
  // Operator wallet too low to cover the global pending queue. Surface
  // this to affiliates so they don't think their payout is forgotten —
  // it's stuck waiting for the platform to refill the payout source.
  return `
    <div class="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
      <div class="flex items-start gap-3">
        <span class="material-symbols-outlined text-amber-400 mt-1">pause_circle</span>
        <div>
          <p class="font-headline text-on-surface font-semibold mb-1">Payouts temporarily paused</p>
          <p class="text-on-surface-variant text-sm">
            The platform's payout wallet is low. Your pending earnings are
            safe — they'll send as soon as the wallet is topped up
            (usually within a day). No action needed on your side.
          </p>
        </div>
      </div>
    </div>
  `;
}

function _pendingKpiCard(amount, underfunded) {
  // Same numbers, different sub-label depending on whether the cron can
  // actually send them.
  const sub = underfunded
    ? `<div class="text-amber-400 text-xs mt-1">payouts paused — see banner above</div>`
    : `<div class="text-on-surface-variant text-xs mt-1">arriving within minutes of next withdrawal</div>`;
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-5">
      <div class="flex items-center gap-2 mb-2">
        <span class="material-symbols-outlined text-on-surface-variant text-base">schedule</span>
        <span class="text-on-surface-variant text-xs uppercase tracking-wider">Pending</span>
      </div>
      <div class="font-headline text-2xl text-on-surface font-semibold">$${(amount || 0).toFixed(2)}</div>
      ${sub}
    </div>
  `;
}

function _eligibilityBanner(affiliations, anyEligible) {
  if (affiliations.length === 0) return '';
  const vaultsList = affiliations.map(a => `<strong>${a.vault.toUpperCase()}</strong>`).join(', ');
  if (anyEligible) {
    return `
      <div class="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-emerald-400 mt-1">check_circle</span>
          <div>
            <p class="font-headline text-on-surface font-semibold mb-1">You're eligible</p>
            <p class="text-on-surface-variant text-sm">
              Keep at least $500 in ${vaultsList} to keep earning. Payouts land in USDC
              within minutes of your referrals' withdrawals.
            </p>
          </div>
        </div>
      </div>
    `;
  }
  return `
    <div class="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
      <div class="flex items-start gap-3">
        <span class="material-symbols-outlined text-red-400 mt-1">warning</span>
        <div>
          <p class="font-headline text-on-surface font-semibold mb-1">Not eligible</p>
          <p class="text-on-surface-variant text-sm">
            Your balance is below $500 in ${vaultsList}. Top up to resume earning.
            Any perf fees from your referrals while you're below threshold are forfeited.
          </p>
        </div>
      </div>
    </div>
  `;
}

function _kpiCard(label, value, icon) {
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-5">
      <div class="flex items-center gap-2 mb-2">
        <span class="material-symbols-outlined text-on-surface-variant text-base">${icon}</span>
        <span class="text-on-surface-variant text-xs uppercase tracking-wider">${label}</span>
      </div>
      <div class="font-headline text-2xl text-on-surface font-semibold">${value}</div>
    </div>
  `;
}

function _linksCard(affiliations) {
  const rows = affiliations.map(a => {
    const vc = VAULT_CONFIGS.find(v => v.key === a.vault);
    const vaultLabel = vc ? vc.name : a.vault.toUpperCase();
    const link = `${LINK_BASE}${a.link_code}`;
    const statusBadge = a.currently_eligible
      ? `<span class="text-emerald-400 text-xs uppercase tracking-wider">Eligible</span>`
      : `<span class="text-red-400 text-xs uppercase tracking-wider">Below $500</span>`;
    return `
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 border border-outline-variant rounded-lg bg-surface-container-low">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-3">
            <span class="font-headline text-on-surface font-semibold">${vaultLabel}</span>
            ${statusBadge}
          </div>
          <code class="text-xs text-secondary break-all">${link}</code>
        </div>
        <button class="copy-link-btn px-4 py-2 bg-primary/20 text-primary border border-primary/30 rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors whitespace-nowrap"
                data-link="${link}">
          Copy link
        </button>
      </div>
    `;
  }).join('');
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-5">
      <h2 class="font-headline text-lg text-on-surface mb-3">Your referral links</h2>
      <div class="flex flex-col gap-3">${rows}</div>
    </div>
  `;
}

function _forfeitCard(forfeit_by_reason) {
  const total = Object.values(forfeit_by_reason).reduce((a, b) => a + b, 0);
  if (total <= 0) return '';
  const reasonLabel = {
    'affiliate_below_threshold': 'You were below $500 when the referral withdrew',
    'below_referred_minimum': 'Referred user had less than $200 deposited',
  };
  const rows = Object.entries(forfeit_by_reason).map(([reason, amt]) => `
    <div class="flex justify-between text-sm">
      <span class="text-on-surface-variant">${reasonLabel[reason] || reason}</span>
      <span class="text-on-surface font-mono">$${amt.toFixed(2)}</span>
    </div>
  `).join('');
  return `
    <div class="bg-surface-container border border-amber-500/20 rounded-xl p-5">
      <h2 class="font-headline text-lg text-on-surface mb-1">Forfeited</h2>
      <p class="text-on-surface-variant text-xs mb-3">
        Total $${total.toFixed(2)} — fees that would have flowed to you but didn't.
      </p>
      <div class="flex flex-col gap-1">${rows}</div>
    </div>
  `;
}

function _payoutsTable(payouts) {
  if (!payouts || payouts.length === 0) {
    return `
      <div class="bg-surface-container border border-outline-variant rounded-xl p-5">
        <h2 class="font-headline text-lg text-on-surface mb-2">Payout history</h2>
        <p class="text-on-surface-variant text-sm">
          No payouts yet. They appear here within minutes of any referred user's withdrawal.
        </p>
      </div>
    `;
  }
  const rows = payouts.map(p => {
    const date = new Date(p.created_at * 1000).toISOString().slice(0, 10);
    const statusBadge = p.forfeit_reason
      ? `<span class="text-amber-400 text-xs">forfeit</span>`
      : p.paid_at
        ? `<span class="text-emerald-400 text-xs">paid</span>`
        : `<span class="text-secondary text-xs">pending</span>`;
    const txLink = p.payout_tx_hash
      ? `<a href="https://hyperliquid.cloud.blockscout.com/tx/${p.payout_tx_hash}" target="_blank" rel="noopener" class="text-primary hover:underline text-xs font-mono">${p.payout_tx_hash.slice(0, 10)}…</a>`
      : `<span class="text-on-surface-variant text-xs">—</span>`;
    return `
      <tr class="border-b border-outline-variant/30">
        <td class="py-2 text-xs text-on-surface-variant">${date}</td>
        <td class="py-2 text-xs text-on-surface">${p.vault.toUpperCase()}</td>
        <td class="py-2 text-xs font-mono text-on-surface-variant">${shortAddr(p.referred_wallet)}</td>
        <td class="py-2 text-xs text-on-surface text-right font-mono">$${(p.affiliate_share_usdc || 0).toFixed(4)}</td>
        <td class="py-2 text-xs text-center">${statusBadge}</td>
        <td class="py-2 text-xs text-right">${txLink}</td>
      </tr>
    `;
  }).join('');
  return `
    <div class="bg-surface-container border border-outline-variant rounded-xl p-5">
      <h2 class="font-headline text-lg text-on-surface mb-3">Payout history</h2>
      <div class="overflow-x-auto">
        <table class="w-full text-left">
          <thead>
            <tr class="border-b border-outline-variant">
              <th class="py-2 text-xs uppercase tracking-wider text-on-surface-variant">Date</th>
              <th class="py-2 text-xs uppercase tracking-wider text-on-surface-variant">Vault</th>
              <th class="py-2 text-xs uppercase tracking-wider text-on-surface-variant">Referred</th>
              <th class="py-2 text-xs uppercase tracking-wider text-on-surface-variant text-right">Amount</th>
              <th class="py-2 text-xs uppercase tracking-wider text-on-surface-variant text-center">Status</th>
              <th class="py-2 text-xs uppercase tracking-wider text-on-surface-variant text-right">Tx</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
}

function _wireCopyButtons() {
  document.querySelectorAll('.copy-link-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const link = btn.dataset.link;
      try {
        await navigator.clipboard.writeText(link);
        const orig = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = orig; }, 1500);
      } catch (e) {
        console.warn('clipboard write failed:', e);
        btn.textContent = 'Failed';
      }
    });
  });
}
